<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import {
  Co2BasketValue,
  Co2QuizQuestion,
  titleRules,
} from '@/models/co2-quiz.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import { countries } from '@/models/country.model';
import CategoriesField from '@/components/categories/CategoriesField.vue';
import { LabelsState } from '@/stores/labels.store';
import { UserLabel } from '@/models/label.model';
import RichTextField from '@/components/common/RichTextField.vue';
import LinksField from '@/components/common/LinksField.vue';

const store = useStore<
  ShiftCMSState & { co2: Co2QuizStoreState; ls: LabelsState }
>();
const questions = computed<Co2QuizQuestion[]>(() => store.state.co2.questions);
const labels = computed<UserLabel[]>(() => store.state.ls.labels);
const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Co2BasketValue>,
  },
});

const valid = ref<boolean>(false);

const value = ref<Co2BasketValue>({
  id: NEW_ID,
  value: 0,
  description: '',
  sources: [],
  country: 'NL',
  overrideQuestions: [],
  notRelevantForlabels: [],
  categories: [],
  ...(props.data || {}),
});

const action = computed<string>(() =>
  value.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveCo2BasketValue = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (value.value.id === NEW_ID) {
      success = await store.dispatch(
        Co2QuizStoreActions.AddCo2BasketValue,
        value.value,
      );
    } else {
      success = await store.dispatch(
        Co2QuizStoreActions.UpdateCo2BasketValue,
        value.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved value',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(Co2QuizStoreActions.GetCo2Values);
  }
};

const updateLanguage = async () => {
  if (value.value.id === NEW_ID) {
    Object.assign(value.value, { title: '', type: 'value' });
  } else {
    const q = await store.dispatch(
      Co2QuizStoreActions.GetCo2Value,
      value.value.id,
    );

    if (q) {
      Object.assign(value.value, { ...q });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(value.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveCo2BasketValue">
    <v-container>
      <h2>{{ $t(action) }} a value <CurrentLanguage /></h2>

      <CategoriesField class="mt-8" v-model="value.categories" :max="1" />

      <v-text-field
        v-model="value.value"
        :rules="titleRules"
        label="Value"
        required
      ></v-text-field>

      <v-autocomplete
        v-model="value.country"
        :items="countries"
        label="Country"
        item-title="text"
        item-value="value"
        :hide-details="true"
      ></v-autocomplete>

      <v-select
        v-model="value.overrideQuestions"
        label="Override questions"
        multiple
        class="mt-8 mb-8"
        hide-details="auto"
        :style="{ background: 'white' }"
        :items="questions.map((q) => ({ title: q.title, value: q }))"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>

      <v-select
        v-model="value.notRelevantForlabels"
        label="Not relevant for labels"
        multiple
        clearable
        class="mt-4"
        hide-details="auto"
        :style="{ background: 'white' }"
        :items="labels.map((q) => ({ title: q.value, value: q }))"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>

      <h3>Read more</h3>
      <RichTextField
        label="Description"
        :height="200"
        v-model="value.description"
        :media="[]"
      />
      <LinksField v-model="value.sources" label="Source" />

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
